import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '@lms/ui';
import type { WhatsAppGroup, WhatsAppGender } from '@lms/shared/types';
import { useCreateWhatsAppGroup, useUpdateWhatsAppGroup } from '../hooks/useWhatsAppGroups';
import { usePrograms } from '../hooks/usePrograms';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface WhatsAppGroupModalProps {
  group?: WhatsAppGroup;
  initialProgramId?: string;
  onClose: () => void;
}

export function WhatsAppGroupModal({ group, initialProgramId, onClose }: WhatsAppGroupModalProps) {
  const isEditing = !!group;
  const { t } = useLanguage();
  const { mutate: create, isPending: isCreating } = useCreateWhatsAppGroup();
  const { mutate: update, isPending: isUpdating } = useUpdateWhatsAppGroup();
  const { data: programsData } = usePrograms();
  const programItems = Array.isArray(programsData?.items) ? programsData.items : [];

  const [formData, setFormData] = useState<Partial<WhatsAppGroup>>(
    group || {
      name: '',
      description: '',
      gender: 'MIXED' as WhatsAppGender,
      maxMembers: 1000,
      inviteLink: '',
      programId: initialProgramId || '',
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && group) {
      update(
        { id: group.id, data: formData },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      create(formData, {
        onSuccess: () => onClose(),
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <div className="fixed inset-0 bg-on-surface/40 backdrop-blur-sm z-50 flex justify-center p-4 animate-fadeIn overflow-y-auto py-12">
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-xl shadow-level4 overflow-hidden animate-slideUp border border-on-surface/5 my-auto">
        <div className="p-8 border-b border-outline-variant/10 bg-surface-container-low/30 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-headline font-bold text-on-surface m-0 tracking-tight">
              <Bilingual
                {...(isEditing
                  ? copy.whatsAppGroupModal.titleEdit
                  : copy.whatsAppGroupModal.titleCreate)}
              />
            </h2>
            <p className="text-xs font-body text-on-surface-variant mt-1 opacity-60 uppercase tracking-widest font-bold">
              <Bilingual {...copy.whatsAppGroupModal.subtitle} />
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-on-surface/10 rounded-full transition-colors"
          >
            <X size={20} className="text-on-surface-variant" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.whatsAppGroupModal.labelName} />
            </label>
            <Input
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderName)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.whatsAppGroupModal.labelProgram} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                value={formData.programId}
                onChange={e => setFormData({ ...formData, programId: e.target.value })}
                required
              >
                <option value="">{t(copy.whatsAppGroupModal.selectProgram)}</option>
                {programItems.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.whatsAppGroupModal.labelGender} />
              </label>
              <select
                className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none h-12"
                value={formData.gender}
                onChange={e =>
                  setFormData({ ...formData, gender: e.target.value as WhatsAppGender })
                }
                required
              >
                <option value="MIXED">{t(copy.whatsAppGroupModal.genderMixed)}</option>
                <option value="IKHWAN">{t(copy.whatsAppGroupModal.genderIkhwan)}</option>
                <option value="AKHWAT">{t(copy.whatsAppGroupModal.genderAkhwat)}</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.whatsAppGroupModal.labelInviteLink} />
            </label>
            <Input
              value={formData.inviteLink || ''}
              onChange={e => setFormData({ ...formData, inviteLink: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderInviteLink)}
              className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.whatsAppGroupModal.labelCapacity} />
              </label>
              <Input
                type="number"
                min={1}
                max={2000}
                value={formData.maxMembers ?? 950}
                onChange={e =>
                  setFormData({
                    ...formData,
                    maxMembers: parseInt(e.target.value) || 950,
                  })
                }
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
                required
              />
              <p className="text-[10px] text-on-surface-variant/70 pl-2 m-0">
                Batas kapasitas tautan (default 950/1000). Jika penuh, sistem otomatis rollover ke
                grup berikutnya.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.whatsAppGroupModal.labelAgeMin} />
              </label>
              <Input
                type="number"
                value={formData.ageMin || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    ageMin: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                placeholder={t(copy.whatsAppGroupModal.placeholderAge)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
                <Bilingual {...copy.whatsAppGroupModal.labelAgeMax} />
              </label>
              <Input
                type="number"
                value={formData.ageMax || ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    ageMax: e.target.value ? parseInt(e.target.value) : undefined,
                  })
                }
                placeholder={t(copy.whatsAppGroupModal.placeholderAge)}
                className="bg-surface-container-low border-none focus:ring-2 focus:ring-primary/20 h-12"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-2 opacity-60">
              <Bilingual {...copy.whatsAppGroupModal.labelDescription} />
            </label>
            <textarea
              className="w-full bg-surface-container-low rounded-xl px-4 py-3 text-sm font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 transition-all outline-none min-h-[100px] resize-none"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder={t(copy.whatsAppGroupModal.placeholderDescription)}
            />
          </div>

          <div className="pt-6 flex justify-end gap-6 items-center border-t border-outline-variant/10">
            <button
              type="button"
              onClick={onClose}
              className="text-[10px] uppercase font-black tracking-[0.2em] text-on-surface-variant hover:text-primary transition-colors"
            >
              <Bilingual {...copy.whatsAppGroupModal.abort} />
            </button>
            <Button
              type="submit"
              className="rounded-2xl px-10 h-14 text-[10px] uppercase font-black tracking-[0.25em] shadow-level2"
              disabled={isPending}
            >
              {isPending ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              <Bilingual
                {...(isEditing
                  ? copy.whatsAppGroupModal.submitEdit
                  : copy.whatsAppGroupModal.submitCreate)}
              />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
